import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";

interface Supplier {
  id: number;
  name: string;
}

interface Bill {
  id: number;
  invoice_no?: string;
  date?: string;
  total?: number;
  paid?: number;
  balance?: number;
  items_count?: number;
  items_preview?: string[];
}

interface BillItem {
  drug_id: number;
  drug_name: string;
  strip_cost: number;
  units_per_strip: number;
  num_strips: number;
  total_units: number;
  cost_per_unit: number;
  line_total: number;
  batch_number: string;
  tax_percentage: number;
  tax_amount: number;
}

export default function PharmacySupplierBillPayment({ supplier, onClose, onPaid }: { supplier: Supplier; onClose: () => void; onPaid: () => void }) {
  const [bills, setBills] = useState<Bill[]>([]);
  const [payMap, setPayMap] = useState<Record<number, string>>({});
  const [receiptMap, setReceiptMap] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [viewingItems, setViewingItems] = useState<number | null>(null);
  const [billItems, setBillItems] = useState<BillItem[]>([]);
  const { showToast } = useToast();

  useEffect(() => {
    if (!supplier?.id) return;
    apiGet(`/api/pharmacy/supplier/${supplier.id}/bills`)
      .then((res: unknown) => setBills(Array.isArray(res) ? (res as Bill[]) : []))
      .catch(() => setBills([]));
  }, [supplier]);

  const viewItems = async (billId: number) => {
    try {
      const items = await apiGet(`/api/pharmacy/supplier/${supplier.id}/bills/${billId}/items`);
      setBillItems(Array.isArray(items) ? (items as BillItem[]) : []);
      setViewingItems(billId);
    } catch {
      showToast({ message: "Failed to load bill items", type: "error" });
    }
  };

  const payBill = async (bill: Bill) => {
    const amount = Number(payMap[bill.id] || 0);
    const receiptNumber = (receiptMap[bill.id] || "").trim();

    if (amount <= 0) return showToast({ message: "Enter valid amount", type: "warning" });
    if (amount > (bill.balance ?? 0)) return showToast({ message: "Payment exceeds balance", type: "error" });

    try {
      setLoading(true);
      await apiPost("/api/pharmacy/supplier-payment", {
        purchase_bill_id: bill.id,
        amount,
        mode: "Cash",
        receipt_number: receiptNumber || null
      });

      const receiptMsg = receiptNumber ? ` (Receipt: ${receiptNumber})` : "";
      showToast({ message: `Payment successful!${receiptMsg}`, type: "success" });
      window.dispatchEvent(new Event("pharmacy:supplierPaid"));
      onPaid();
    } catch {
      showToast({ message: "Payment failed", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
    background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', 
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };

  const modalStyle: React.CSSProperties = {
    background: '#fff', borderRadius: '20px', padding: '24px', 
    width: '100%', maxWidth: viewingItems ? '850px' : '750px', 
    maxHeight: '85vh', display: 'flex', flexDirection: 'column', 
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  };

  const inputStyle = { padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem' }}>Supplier Payment</h3>
            <div style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px' }}>Outstanding bills for <strong>{supplier.name}</strong></div>
          </div>
          <Button variant="ghost" onClick={onClose} style={{ fontSize: '1.5rem', padding: '0 8px' }}>×</Button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {viewingItems ? (
            <div>
              <Button variant="outline" size="sm" onClick={() => setViewingItems(null)} style={{ marginBottom: '16px' }}>
                ← Back to Bills
              </Button>
              <h4 style={{ margin: '0 0 12px 0', color: '#334155' }}>Bill Items</h4>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Drug Name</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Batch #</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Strips</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>MRP (Pack)</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>GST %</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Tax</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billItems.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No items found in this bill</td>
                      </tr>
                    ) : (
                      billItems.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#0f172a', fontWeight: 500 }}>{item.drug_name}</td>
                          <td style={{ padding: '12px', fontSize: '0.85rem', color: '#64748b' }}>{item.batch_number || "—"}</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155' }}>{item.num_strips}</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155' }}>₹{item.strip_cost.toFixed(2)}</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155' }}>{item.tax_percentage}%</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155' }}>₹{item.tax_amount.toFixed(2)}</td>
                          <td style={{ padding: '12px', fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', textAlign: 'right' }}>₹{item.line_total.toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {bills.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                  No outstanding bills found for this supplier.
                </div>
              ) : (
                bills.map((b) => (
                  <div key={b.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ fontSize: '1rem', color: '#0f172a' }}>{b.invoice_no || "Unnamed Invoice"}</strong>
                          <span style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px' }}>{b.date}</span>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                          {(b.items_count ?? 0) > 0 ? (
                            <span style={{ cursor: 'pointer', color: '#3b82f6' }} onClick={() => viewItems(b.id)}>
                              View {b.items_count} items ({(b.items_preview ?? []).join(", ").slice(0, 30)}...)
                            </span>
                          ) : "No items listed"}
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>₹{b.total}</div>
                        <div style={{ fontSize: '0.8rem', color: '#16a34a' }}>Paid: ₹{b.paid}</div>
                        <div style={{ fontSize: '0.85rem', color: '#dc2626', fontWeight: 600, marginTop: '2px' }}>Balance: ₹{b.balance}</div>
                      </div>
                    </div>

                    {(b.balance ?? 0) > 0 && (
                      <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', marginTop: '8px' }}>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Receipt # (Optional)</label>
                          <input
                            type="text"
                            placeholder="e.g. REC-001"
                            value={receiptMap[b.id] || ""}
                            onChange={(e) => setReceiptMap({ ...receiptMap, [b.id]: e.target.value })}
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Payment Amount</label>
                          <input
                            type="number"
                            min={1}
                            max={b.balance}
                            placeholder={`Max ₹${b.balance}`}
                            value={payMap[b.id] || ""}
                            onChange={(e) => setPayMap({ ...payMap, [b.id]: e.target.value })}
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div style={{ alignSelf: 'flex-end' }}>
                          <Button variant="primary" onClick={() => payBill(b)} disabled={loading} isLoading={loading && payMap[b.id] !== undefined}>
                            Submit Payment
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={loading}>Close Window</Button>
        </div>
      </div>
    </div>
  );
}
